import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../BusinessObjects/Redux/app/store";
import { useView } from "../ResourceViewPage";
import { Box, Button, Container, Typography } from "@mui/material";
import RenderForm from "../../../Components/Esaedro/RenderForm/RenderForm";
import { ResourceNames } from "../../../Utils/ResourceNames";
import { useQuery } from "react-query";
import cubeApiClient from "../../../BusinessObjects/cubeApiService";
import { Form } from "../../../Models/SysConf/Form";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { RoutePath } from "../../../Utils/RoutePath";
import RenderFormNew from "../../../Components/Esaedro/RenderForm/RenderFormNew";
import { CustomColumn } from "../../../Models/Project/CustomColumn";
import { DataGrid } from "devextreme-react";
import { Column, Editing, FilterRow } from "devextreme-react/data-grid";
import { ResourceViewFilter } from "../../../Models/SysConf/ResourceViewFilter";
import notify from "devextreme/ui/notify";
import { ResourceView } from "../../../Models/SysConf/ResourceView";
import Loader from "../../../Utils/Loader";

type Props = {};

type ContextType = {
  objEdit: ResourceView;
  setObjEdit: React.Dispatch<React.SetStateAction<ResourceView>>;
};

export default function FiltersOnFields(props: Props) {
  // Redux
  const tokenApi = useSelector((state: RootState) => state.user.tokenAPI);
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  // React Router DOM
  const { view, setView } = useView();
  const navigate = useNavigate();
  const location = useLocation();

  const columnsDataGridResourcesViewFilters: CustomColumn[] = [
    {
      caption: "Id",
      dataField: "Filter_ID",
    },
    {
      caption: "Risorsa",
      dataField: "ResourceName",
    },
    {
      caption: "Campo",
      dataField: "FieldName",
    },
    {
      caption: "Operatore",
      dataField: "Operator",
    },
    {
      caption: "Valore",
      dataField: "Value",
    },
    {
      caption: "Operatore filtro precedente",
      dataField: "PreviousFilterOperator",
    },
    {
      caption: "Risorsa comparazione",
      dataField: "Compare_ResourceName",
    },
    {
      caption: "Campo comparazione",
      dataField: "Compare_FieldName",
    },
  ];

  // hooks
  const [isNewFormVisible, setNewFormVisible] = React.useState<boolean>(false);
  const [isEditFormVisible, setEditFormVisible] =
    React.useState<boolean>(false);
  const [objEdit, setObjEdit] = React.useState<ResourceView | {}>({});
  const [initalView, setInitialView] = React.useState<ResourceView>({
    ...view,
  });

  React.useEffect(() => {
    if (
      location.pathname ===
      `${RoutePath.RESOURCES_VIEWS}/${view?.View_ID}/fiters_on_fields`
    ) {
      setEditFormVisible(false);
    }
  }, [location]);

  // Chiamate API
  const {
    data: resourcesViewsFilters,
    isLoading: isLoadingResourcesViewsFilters,
    isError: isErrorResourcesViewsFilters,
    refetch: refetchResourcesViewsFilters,
    isRefetching: isRefetchingResourcesViewsFilters,
  } = useQuery(
    ["resourcesViewsFilters", view?.View_ID, tokenApi],
    () => {
      var urlToUse = `api/sys/${ResourceNames.ResourcesViewsFilters}?RecordSelection=View_ID=${view?.View_ID}`;
      const service = new cubeApiClient();

      return service.getItems(urlToUse, tokenApi);
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const { data: form, isLoading: isLoadingForm } = useQuery(
    ["form", ResourceNames.ResourcesViewes, userProfile?.customer_ID, tokenApi],
    () => {
      const service = new cubeApiClient();

      return service
        .getItems(
          `api/sys/${ResourceNames.Resources}?RecordSelection=ResourceName='${ResourceNames.ResourcesViewes}'`,
          tokenApi
        )
        .then(
          (data: any) => {
            let urlForm = `api/sys/${ResourceNames.Forms}/query?ResourceId=${data[0].Resource_ID}`;

            return service.getItems(urlForm, tokenApi).then(
              (data: any) => {
                return data[0];
              },
              (error: any) => {
                return undefined;
              }
            );
          },
          (error: any) => {
            return undefined;
          }
        );
    },
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const {
    data: formResourcesViewsFilters,
    isLoading: isLoadingFormResourcesViewsFilters,
  } = useQuery(
    [
      "form",
      ResourceNames.ResourcesViewsFilters,
      userProfile?.customer_ID,
      tokenApi,
    ],
    () => {
      const service = new cubeApiClient();

      return service
        .getItems(
          `api/sys/${ResourceNames.Resources}?RecordSelection=ResourceName='${ResourceNames.ResourcesViewsFilters}'`,
          tokenApi
        )
        .then(
          (data: any) => {
            let urlForm = `api/sys/${ResourceNames.Forms}/query?ResourceId=${data[0].Resource_ID}`;

            return service.getItems(urlForm, tokenApi).then(
              (data: any) => {
                return data[0];
              },
              (error: any) => {
                return undefined;
              }
            );
          },
          (error: any) => {
            return undefined;
          }
        );
    },
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const formResourceViewFilter: Form | undefined = React.useMemo(() => {
    if (formResourcesViewsFilters) {
      let tmpForm: Form = formResourcesViewsFilters;

      tmpForm.FormsGroups.forEach((fg) => {
        fg.FormsFields.forEach((ff) => {
          if (ff.FieldName === "View_ID") {
            ff.Is_Editable = false;
          }
        });
      });

      return tmpForm;
    }

    return undefined;
  }, [formResourcesViewsFilters]);

  // Elimino il filtro
  const deleteResourceViewFilter = (e: ResourceViewFilter) => {
    const service = new cubeApiClient();

    service
      .deleteObject(
        e,
        `api/sys/${ResourceNames.ResourcesViewsFilters}`,
        tokenApi
      )
      .then(
        (data) => {
          refetchResourcesViewsFilters();
          notify("Filtro eliminato con successo", "success", 1000);
        },
        (error) => {
          refetchResourcesViewsFilters();
          notify("Errore nell'eliminazione del filtro", "error", 1000);
        }
      );
  };

  // Salvo il nuovo filtro
  const saveNewResourceViewFilter = () => {
    const service = new cubeApiClient();

    service
      .updateObject(
        view,
        `api/sys/${ResourceNames.ResourcesViewsFilters}`,
        tokenApi
      )
      .then(
        (data) => {
          setNewFormVisible(false);
          refetchResourcesViewsFilters();
          notify("Filtro salvato con successo", "success", 1000);
        },
        (error) => {
          refetchResourcesViewsFilters();
          notify("Errore nel salvataggio del filtro", "error", 1000);
        }
      );
  };

  // ! Inizio DOM
  return (
    <>
      {/* Titolo della pagina */}
      <Typography
        variant="h6"
        color={"primary.main"}
        style={{ fontWeight: 600, textAlign: "center" }}
        className="pt-3"
      >
        FILTRI SU CAMPI
      </Typography>

      {/* Gestione del Loader  */}
      {view === undefined ||
      view === null ||
      resourcesViewsFilters === undefined ||
      resourcesViewsFilters === null ? (
        <Loader />
      ) : (
        <Container maxWidth="lg" sx={{ mt: 5 }}>
          {/* Bottone aggiungi, visulizzo il form e devisualizzo la DataGrid */}

          {!isNewFormVisible && !isEditFormVisible && (
            <Box sx={{ width: "100%", mb: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setNewFormVisible(true);
                  setView(initalView);
                }}
              >
                Aggiungi
              </Button>
            </Box>
          )}
          <Box sx={{ width: "100%", height: "100%" }}>
            {!isNewFormVisible ? (
              <>
                {!isEditFormVisible ? (
                  <DataGrid
                    dataSource={resourcesViewsFilters || []}
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={true}
                    className="mb-3"
                    onRowRemoved={(e) => {
                      deleteResourceViewFilter(e.data as ResourceViewFilter);
                    }}
                    onEditingStart={(e) => {
                      setObjEdit(e.data);
                      setEditFormVisible(true);
                      navigate("edit");
                    }}
                  >
                    <FilterRow visible={true} />
                    {columnsDataGridResourcesViewFilters.map(
                      (column: CustomColumn, i: number) => (
                        <Column
                          key={i}
                          dataField={column.dataField}
                          caption={column.caption}
                        />
                      )
                    )}
                    <Editing
                      mode="row"
                      allowUpdating={true}
                      allowDeleting={true}
                      confirmDelete={true}
                      useIcons={true}
                      startEditAction="click"
                    />
                  </DataGrid>
                ) : (
                  <Outlet context={{ objEdit, setObjEdit }} />
                )}
              </>
            ) : (
              <RenderFormNew
                showTitle={false}
                isNew={true}
                form={formResourceViewFilter}
                myObj={view}
                saveNewObject={(e) => {
                  setView(e);
                }}
              />
            )}

            {isNewFormVisible ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  flexDirection: "row",
                }}
              >
                {/* Bottone di Anulla, mi riporta alla DataGrid */}
                <Button
                  variant="contained"
                  color="error"
                  className="me-2"
                  onClick={() => setNewFormVisible(false)}
                >
                  Annulla
                </Button>

                {/* Bottone salva */}
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => saveNewResourceViewFilter()}
                >
                  Salva
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  flexDirection: "row",
                }}
              >
                {/* Bottone di Anulla, mi riporta al form iniziale */}

                {!isEditFormVisible && !isNewFormVisible && (
                  <>
                    <NavLink
                      to={`${RoutePath.RESOURCES_VIEWS}/${view.View_ID}`}
                    >
                      <Button
                        variant="contained"
                        color="error"
                        className="me-2"
                        onClick={() => setNewFormVisible(false)}
                      >
                        Torna Indietro
                      </Button>
                    </NavLink>

                    {/* Bottone salva, mi riporta alla home*/}
                    <NavLink to={RoutePath.RESOURCES_VIEWS}>
                      <Button variant="contained" color="success">
                        Torna alla griglia
                      </Button>
                    </NavLink>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Container>
      )}
    </>
  );
}

export function useFiltersObjEdit() {
  return useOutletContext<ContextType>();
}
